import { create } from "zustand";
import { persist } from "zustand/middleware";

export const AVAILABLE_MODELS = [
	{ id: "default", label: "GPT-OSS 20B" },
	{ id: "large", label: "GPT-OSS 120B" },
	{ id: "yandexgpt/latest", label: "YandexGPT" },
	{ id: "yandexgpt-lite/latest", label: "YandexGPT Lite" },
] as const;

export interface EntityRef {
	entityType: string;
	entityId: string;
	displayText: string;
}

export type FsPanelState = "collapsed" | "half" | "full";

export interface PendingApproval {
	runId: string;
	stepId: string;
	description: string;
	modifiedFiles?: { path: string; status: string }[];
}

export interface ShellEntry {
	command: string;
	output: string;
	isError: boolean;
	ts: number;
}

// ── Layout constants ──────────────────────────────

const PANEL_WIDTH_MIN = 320;
const PANEL_WIDTH_MAX = 600;
const FS_HEIGHT_MIN = 120;
const FS_HEIGHT_MAX = 800;
const SHELL_BUFFER_MAX = 200;

// ── Store ─────────────────────────────────────────

interface AgentPanelState {
	isOpen: boolean;
	width: number;
	agentPartyId: string;
	selectedModel: string;
	initialPrompt: string;
	initialEntities: EntityRef[];
	conversationId: string;

	sessionId: string;
	fsPanelState: FsPanelState;
	fsPanelHeight: number;
	fsPreviewPath: string | null;
	fsExpandedPaths: Set<string>;
	fsShowMemory: boolean;

	previewOpen: boolean;
	previewRefreshKey: number;

	profileId: string;
	layoutMode: "sidebar" | "split" | "fullscreen";
	autoPreviewTriggered: boolean;

	toggle: () => void;
	setOpen: (open: boolean) => void;
	setWidth: (w: number) => void;
	setAgentPartyId: (id: string) => void;
	setSelectedModel: (m: string) => void;
	setInitialPrompt: (p: string) => void;
	setInitialEntities: (e: EntityRef[]) => void;
	setConversationId: (id: string) => void;
	newConversation: () => void;

	setSessionId: (id: string) => void;
	setFsPanelState: (s: FsPanelState) => void;
	setFsPanelHeight: (h: number) => void;
	setFsPreviewPath: (p: string | null) => void;
	toggleFsPath: (path: string) => void;
	toggleFsMemory: () => void;

	setPreviewOpen: (open: boolean) => void;
	refreshPreview: () => void;

	setProfileId: (id: string) => void;
	setLayoutMode: (m: "sidebar" | "split" | "fullscreen") => void;
	setAutoPreviewTriggered: (v: boolean) => void;

	pendingApproval: PendingApproval | null;
	setPendingApproval: (a: PendingApproval | null) => void;

	shellEntries: ShellEntry[];
	addShellEntry: (e: Omit<ShellEntry, "ts">) => void;
	clearShellEntries: () => void;
}

export const useAgentStore = create<AgentPanelState>()(
	persist(
		(set) => ({
			isOpen: false,
			width: 400,
			agentPartyId: "",
			selectedModel: "",
			initialPrompt: "",
			initialEntities: [],
			conversationId: "",

			sessionId: "",
			fsPanelState: "collapsed" as FsPanelState,
			fsPanelHeight: 300,
			fsPreviewPath: null,
			fsExpandedPaths: new Set<string>(),
			fsShowMemory: false,

			previewOpen: false,
			previewRefreshKey: 0,

			profileId: "",
			layoutMode: "sidebar" as const,
			autoPreviewTriggered: false,

			toggle: () => set((s) => ({ isOpen: !s.isOpen })),
			setOpen: (open) => set({ isOpen: open }),
			setWidth: (w) =>
				set({ width: Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, w)) }),
			setAgentPartyId: (id) => set({ agentPartyId: id }),
			setSelectedModel: (m) => set({ selectedModel: m }),
			setInitialPrompt: (p) => set({ initialPrompt: p }),
			setInitialEntities: (e) => set({ initialEntities: e }),
			setConversationId: (id) => set({ conversationId: id }),
			newConversation: () => set({ conversationId: "" }),

			setSessionId: (id) => set({ sessionId: id }),
			setFsPanelState: (s) => set({ fsPanelState: s }),
			setFsPanelHeight: (h) =>
				set({ fsPanelHeight: Math.max(FS_HEIGHT_MIN, Math.min(FS_HEIGHT_MAX, h)) }),
			setFsPreviewPath: (p) => set({ fsPreviewPath: p }),
			toggleFsPath: (path) =>
				set((s) => {
					const next = new Set(s.fsExpandedPaths);
					next.has(path) ? next.delete(path) : next.add(path);
					return { fsExpandedPaths: next };
				}),
			toggleFsMemory: () =>
				set((s) => ({ fsShowMemory: !s.fsShowMemory })),

			setPreviewOpen: (open) => set({ previewOpen: open }),
			refreshPreview: () =>
				set((s) => ({ previewRefreshKey: s.previewRefreshKey + 1 })),

			setProfileId: (id) => set({ profileId: id }),
			setLayoutMode: (m) => set({ layoutMode: m }),
			setAutoPreviewTriggered: (v) => set({ autoPreviewTriggered: v }),

			pendingApproval: null,
			setPendingApproval: (a) => set({ pendingApproval: a }),

			shellEntries: [],
			addShellEntry: (e) =>
				set((s) => ({
					shellEntries: [
						...s.shellEntries.slice(-(SHELL_BUFFER_MAX - 1)),
						{ ...e, ts: Date.now() },
					],
				})),
			clearShellEntries: () => set({ shellEntries: [] }),
		}),
		{
			name: "omnios-agent",
			partialize: (s) => ({
				isOpen: s.isOpen,
				width: s.width,
				agentPartyId: s.agentPartyId,
				selectedModel: s.selectedModel,
				conversationId: s.conversationId,
				sessionId: s.sessionId,
				fsPanelState: s.fsPanelState,
				fsPanelHeight: s.fsPanelHeight,
				fsShowMemory: s.fsShowMemory,
				profileId: s.profileId,
				layoutMode: s.layoutMode,
			}),
		},
	),
);
